import { BORDER_SHIFT, POSITIONS, PREFERRED_POSITIONS } from './constants';

/**
 * Вычисляет смещение элемента DOM относительно document. По структуре результат аналогичен
 * getBoundingClientRect.
 *
 * @param element элемент для которого надо опредлить смещение.
 * @returns {*} результат.
 */
const getElementDocumentOffset = element => {
  if (!element) {
    return { top: null, left: null };
  }
  if (!element.getClientRects().length) {
    return { top: 0, left: 0 };
  }
  const { top, bottom, left, right } = element.getBoundingClientRect();
  const { pageXOffset, pageYOffset } = element.ownerDocument.defaultView;
  return {
    top: Math.round(top + pageYOffset),
    bottom: Math.round(bottom + pageYOffset),
    left: Math.round(left + pageXOffset),
    right: Math.round(right + pageXOffset),
  };
};

/**
 * Вычисляет размеры элемента DOM.
 *
 * @param element
 * @returns {{width: number, height: number}}
 */
const getElementSize = element => {
  const { top, right, bottom, left } = element.getBoundingClientRect();

  return {
    width: Math.round(right - left),
    height: Math.round(bottom - top),
  };
};

/**
 * Определяет размеры элемента DOM, как если бы он находился в самом начале страницы и ему не мешало
 * его текущее положение или положение других элементов относительно него.
 *
 * @param element элемент DOM, размеры которого необходимо узнать.
 * @returns {{width: number, height: number}}
 */
const getPureElementSize = element => {
  const clonedElement = element.cloneNode(true);
  clonedElement.style.visibility = 'hidden';
  clonedElement.style.display = 'block';
  clonedElement.style.position = 'absolute';
  clonedElement.style.top = 0;
  clonedElement.style.right = 'auto';
  clonedElement.style.bottom = 'auto';
  clonedElement.style.left = 0;
  document.body.appendChild(clonedElement);
  const { top, right, bottom, left } = clonedElement.getBoundingClientRect();
  document.body.removeChild(clonedElement);
  return {
    width: Math.round(right - left),
    height: Math.round(bottom - top),
  };
};

/**
 * Определяет реальную высоту хэдера, так как событие возвращает неверное значение при
 * изменении размеров окна.
 */
const getHeaderRealHeight = () => {
  const elements = document.getElementsByClassName('Header');

  if (elements.length) {
    return getElementSize(elements[0]).height;
  }
  return 0;
};

/**
 * Вычисляет общие параметры позиционирования.
 *
 * @param floating возможно ли автоматическое смещение поповера.
 * @param popoverElement DOM-элемент поповера.
 * @param childElement DOM-элемент, к которому поповер привязан.
 * @param pointerElement DOM-элемент указателя поповера.
 * @returns {
 *  {
 *    popoverSize: {width: number, height: number},
 *    popoverHalfWidth: number,
 *    popoverHalfHeight: number,
 *    pointerHalfWidth: number,
 *    pointerHalfHeight: number,
 *    childSize: {width: number, height: number},
 *    childHalfWidth: number,
 *    childHalfHeight: number,
 *    childOffset: {top, left}|{top, bottom, left, right},
 *    childCenterX: *,
 *    childCenterY: *,
 *    pageWidth: HTMLElement.clientWidth,
 *    pageHeight: HTMLElement.clientHeight,
 *    windowWidth: Window.innerWidth,
 *    windowHeight: Window.innerHeight,
 *    scrollY: number,
 *  }
 * }
 */
const getPositionParams = (
  floating,
  popoverElement,
  childElement,
  pointerElement
) => {
  const { clientWidth: pageWidth, clientHeight: pageHeight } = document.body;
  const {
    innerWidth: windowWidth,
    innerHeight: windowHeight,
    scrollY,
  } = window;

  let result = {
    pageWidth,
    pageHeight,
    windowWidth,
    windowHeight,
    scrollY,
  };

  if (popoverElement) {
    const popoverSize = floating
      ? getPureElementSize(popoverElement)
      : getElementSize(popoverElement);
    const popoverHalfWidth = Math.round(0.5 * popoverSize.width);
    const popoverHalfHeight = Math.round(0.5 * popoverSize.height);

    result = {
      ...result,
      popoverSize,
      popoverHalfWidth,
      popoverHalfHeight,
    };
  }

  if (childElement) {
    const childSize = getElementSize(childElement);
    const childHalfWidth = Math.round(0.5 * childSize.width);
    const childHalfHeight = Math.round(0.5 * childSize.height);
    const childRect = childElement.getBoundingClientRect();
    const childOffset = getElementDocumentOffset(childElement);
    const childCenterX = childOffset.left + childHalfWidth;
    const childCenterY = childOffset.top + childHalfHeight;

    result = {
      ...result,
      childSize,
      childHalfWidth,
      childHalfHeight,
      childRect,
      childOffset,
      childCenterX,
      childCenterY,
    };
  }

  if (pointerElement) {
    const pointerSize = getElementSize(pointerElement);
    const pointerHalfWidth = Math.round(0.5 * pointerSize.width);
    const pointerHalfHeight = Math.round(0.5 * pointerSize.height);

    result = {
      ...result,
      pointerHalfWidth,
      pointerHalfHeight,
    };
  }

  return result;
};

/**
 * Вычисляются некоторые значения по параметрам позиционирования.
 *
 * @param positionParams параметры позиционирования.
 * @returns {{
 *   leftBorderCrossed: boolean,
 *   rightBorderCrossed: boolean,
 *   leftSideEnoughSpace: boolean,
 *   rightSideEnoughSpace: boolean,
 *   screenWide: boolean
 * }}
 */
const getPositionAnalytics = positionParams => {
  const {
    pageWidth,
    popoverSize,
    childOffset,
    childCenterX,
    popoverHalfWidth,
  } = positionParams;

  // Может ли поповер при стандартном позиционировании пересечь ЛЕВУЮ границу документа.
  const leftBorderCrossed = childCenterX - popoverHalfWidth < BORDER_SHIFT;
  // Может ли поповер при стандартном позиционировании пересечь ПРАВУЮ границу документа.
  const rightBorderCrossed =
    childCenterX + popoverHalfWidth + BORDER_SHIFT > pageWidth;
  // Достаточно ли для поповера места СЛЕВА от целевого элемента.
  const leftSideEnoughSpace =
    childOffset.right - popoverSize.width - BORDER_SHIFT >= 0;
  // Достаточно ли для поповера места СПРАВА от целевого элемента.
  const rightSideEnoughSpace =
    childOffset.left + popoverSize.width + BORDER_SHIFT <= pageWidth;
  // Заполняет ли поповер весь экран по ширине (с учетом отступов от краев).
  const screenWide = popoverSize.width >= pageWidth - 2 * BORDER_SHIFT;

  return {
    leftBorderCrossed,
    rightBorderCrossed,
    leftSideEnoughSpace,
    rightSideEnoughSpace,
    screenWide,
  };
};

/**
 * Определяет, находится ли элемент DOM в пределах страницы (по умолчанию) или окна браузера
 * (для модалок). Под страницей подразумевается весь контент: видимый + тот до которого пользователь
 * может доскроллить.
 *
 * @param position позиция поповера.
 * @param modal флаг, указывающий на то, что поповер открывается для модального окна.
 * @param headerHeight высота хэдера mos.ru.
 * @param positionParams набор параметров позиционирования.
 * @returns {boolean}
 */
const isPopoverFitsPageOrWindow = (
  position,
  modal,
  headerHeight,
  positionParams
) => {
  const {
    popoverSize,
    popoverHalfHeight,
    childRect,
    childOffset,
    childCenterY,
    pageWidth,
    pageHeight,
    windowWidth,
    windowHeight,
    scrollY,
  } = positionParams;

  const rect = modal ? childRect : childOffset;

  const headerCorrection =
    headerHeight === 0 ? BORDER_SHIFT : scrollY + headerHeight;

  const topLimit = modal ? BORDER_SHIFT : headerCorrection;

  const rightLimit = modal
    ? windowWidth - BORDER_SHIFT
    : pageWidth - BORDER_SHIFT;

  const bottomLimit = modal
    ? windowHeight - BORDER_SHIFT
    : pageHeight - BORDER_SHIFT;

  switch (position) {
    case POSITIONS.TOP:
      return (
        // TOP side
        rect.top - popoverSize.height >= topLimit &&
        // RIGHT & LEFT sides
        popoverSize.width <= rightLimit
      );

    case POSITIONS.LEFT:
      return (
        // TOP side
        childCenterY - popoverHalfHeight >= topLimit &&
        // BOTTOM side
        childCenterY + popoverHalfHeight <= bottomLimit &&
        // LEFT side
        rect.left - popoverSize.width >= 0
      );

    case POSITIONS.BOTTOM:
      return (
        // BOTTOM side
        rect.bottom + popoverSize.height <= bottomLimit &&
        // RIGHT & LEFT sides
        popoverSize.width <= rightLimit
      );

    case POSITIONS.RIGHT:
      return (
        // TOP side
        childCenterY - popoverHalfHeight >= topLimit &&
        // BOTTOM side
        childCenterY + popoverHalfHeight <= bottomLimit &&
        // LEFT side
        rect.right + popoverSize.width <= rightLimit
      );

    default:
      throw new Error('Unsupported position value');
  }
};

/**
 * Определяет стиль поповера.
 *
 * @param position текущая позиция поповера.
 * @param floating возможно ли автоматическое смещение поповера.
 * @param popoverElement DOM-элемент поповера.
 * @param childElement DOM-элемент, к которому поповер привязан.
 * @returns {{
 *   top: string,
 *   right: string,
 *   bottom: string,
 *   left: string,
 *   transform: string
 * }}
 */
const getPopoverStyle = (position, floating, popoverElement, childElement) => {
  const positionParams = getPositionParams(
    floating,
    popoverElement,
    childElement
  );

  const {
    leftBorderCrossed,
    rightBorderCrossed,
    leftSideEnoughSpace,
    rightSideEnoughSpace,
    screenWide,
  } = getPositionAnalytics(positionParams);

  const {
    popoverSize,
    popoverHalfWidth,
    popoverHalfHeight,
    childSize,
    childCenterX,
    childHalfHeight,
    childOffset,
  } = positionParams;

  const style = {
    top: 'auto',
    right: 'auto',
    bottom: 'auto',
    left: 'auto',
    transform: 'none',
  };

  // Вертикальные координаты
  switch (position) {
    case POSITIONS.TOP:
      if (floating && screenWide) {
        style.top = `${childOffset.top}px`;
        style.transform = 'translateY(-100%)';
      } else {
        style.top = `${childOffset.top - popoverSize.height}px`;
      }
      break;

    case POSITIONS.BOTTOM:
      style.top = `${childOffset.top + childSize.height}px`;
      break;

    case POSITIONS.LEFT:
    case POSITIONS.RIGHT:
      style.top = `${childOffset.top + childHalfHeight - popoverHalfHeight}px`;
      break;

    default:
      throw new Error('Unsupported position value');
  }

  // Горизонтальные координаты
  switch (position) {
    case POSITIONS.TOP:
    case POSITIONS.BOTTOM:
      if (floating && leftBorderCrossed) {
        if (rightSideEnoughSpace) {
          style.left = `${childOffset.left}px`;
        } else {
          style.left = `${BORDER_SHIFT}px`;
          if (screenWide) {
            style.right = `${BORDER_SHIFT}px`;
          }
        }
      } else if (floating && rightBorderCrossed) {
        if (leftSideEnoughSpace) {
          style.left = `${childOffset.right - popoverSize.width}px`;
        } else {
          style.right = `${BORDER_SHIFT}px`;
          if (screenWide) {
            style.left = `${BORDER_SHIFT}px`;
          }
        }
      } else {
        style.left = `${childCenterX - popoverHalfWidth}px`;
      }
      break;

    case POSITIONS.LEFT:
      style.left = `${childOffset.left - popoverSize.width}px`;
      break;

    case POSITIONS.RIGHT:
      style.left = `${childOffset.left + childSize.width}px`;
      break;

    default:
      throw new Error('Unsupported position value');
  }

  return style;
};

/**
 * Определяет стиль указателя поповера.
 *
 * @param position позиция поповера.
 * @param floating возможно ли автоматическое смещение поповера.
 * @param popoverElement DOM-элемент поповера.
 * @param childElement DOM-элемент, к которому поповер привязан.
 * @param pointerElement DOM-элемент указателя поповера.
 * @returns {{top: string, right: string, bottom: string, left: string}}
 */
const getPopoverPointerStyle = (
  position,
  floating,
  popoverElement,
  childElement,
  pointerElement
) => {
  const positionParams = getPositionParams(
    floating,
    popoverElement,
    childElement,
    pointerElement
  );

  const {
    leftBorderCrossed,
    rightBorderCrossed,
    leftSideEnoughSpace,
    rightSideEnoughSpace,
  } = getPositionAnalytics(positionParams);

  const {
    pageWidth,
    popoverSize,
    pointerHalfWidth,
    pointerHalfHeight,
    childHalfWidth,
    childCenterX,
  } = positionParams;

  const style = {
    top: 'auto',
    right: 'auto',
    bottom: 'auto',
    left: 'auto',
  };

  // Вертикальные координаты
  switch (position) {
    case POSITIONS.TOP: {
      style.bottom = 0;
      break;
    }

    case POSITIONS.RIGHT:
    case POSITIONS.LEFT: {
      style.top = `calc(50% - ${pointerHalfHeight}px)`;
      break;
    }

    case POSITIONS.BOTTOM: {
      style.top = 0;
      break;
    }

    default:
      throw new Error('Unsupported position value');
  }

  // Горизонтальные координаты
  switch (position) {
    case POSITIONS.TOP:
    case POSITIONS.BOTTOM: {
      if (floating && leftBorderCrossed) {
        if (rightSideEnoughSpace) {
          style.left = `${childHalfWidth - pointerHalfWidth}px`;
        } else {
          style.left = `${childCenterX - pointerHalfWidth - BORDER_SHIFT}px`;
        }
      } else if (floating && rightBorderCrossed) {
        if (leftSideEnoughSpace) {
          style.left = `${popoverSize.width -
            childHalfWidth -
            pointerHalfWidth}px`;
        } else {
          style.right = `${pageWidth -
            childCenterX -
            pointerHalfWidth -
            BORDER_SHIFT}px`;
        }
      } else {
        style.left = `calc(50% - ${pointerHalfWidth}px)`;
      }
      break;
    }

    case POSITIONS.RIGHT: {
      style.left = 0;
      break;
    }

    case POSITIONS.LEFT: {
      style.right = 0;
      break;
    }

    default:
      throw new Error('Unsupported position value');
  }

  return style;
};

/**
 * Определяет позицию поповера таким образом, чтобы он не выходил за пределы страницы.
 *
 * @param initialPosition изначальная позиция поповера, задаваемая через его props.
 * @param modal флаг, указывающий на то, что поповер открывается для модального окна.
 * @param floating возможно ли автоматическое смещение поповера.
 * @param headerHeight высота хэдера mos.ru.
 * @param popoverElement DOM-элемент поповера.
 * @param childElement DOM-элемент, к которому поповер привязан.
 * @returns {*} позиция для поповера.
 */
const getPopoverPosition = (
  initialPosition,
  modal,
  floating,
  headerHeight,
  popoverElement,
  childElement
) => {
  if (!floating) {
    return initialPosition;
  }

  const positionParams = getPositionParams(
    floating,
    popoverElement,
    childElement
  );

  // Проверить, помещается ли поповер с изначальной позицией
  if (
    isPopoverFitsPageOrWindow(
      initialPosition,
      modal,
      headerHeight,
      positionParams
    )
  ) {
    return initialPosition;
  }

  // Проверять остальные позиции в порядке приоритета
  const positions = PREFERRED_POSITIONS[initialPosition];
  for (let i = 0; i < positions.length; i += 1) {
    if (
      isPopoverFitsPageOrWindow(
        positions[i],
        modal,
        headerHeight,
        positionParams
      )
    ) {
      return positions[i];
    }
  }

  // Если новая позиция не найдена - оставляем поповер на месте.
  // Важно именно найти подходящую позицию, когда поповер ПОЛНОСТЬЮ находится в пределах окна, иначе
  // он будет прыгать при каждой перерисовке.
  return initialPosition;
};

export {
  getElementDocumentOffset,
  getHeaderRealHeight,
  getPopoverPosition,
  getPopoverStyle,
  getPopoverPointerStyle,
};
